import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { type ColDef } from 'ag-grid-community'
import { useMemo } from 'react'

import type { GiftCardStatusType, GiftCardRedemptionStatusType } from '@/api/admin-gift-card-types'
import { useDeleteGiftCard } from '@/api/api-mutations'
import type { EndpointResponse } from '@/api/common'
import { Endpoint } from '@/api/common'
import { preloadGetGiftCard, useGetGiftCard } from '@/api/react-query'
import { Container, ContainerHeader } from '@/components/base/container'
import { IconButton } from '@/components/base/icon-button'
import { usePrompt } from '@/components/base/prompt'
import { SectionRow } from '@/components/base/section-row'
import { StatusCell, type StatusColor } from '@/components/base/status-cell'
import { toast } from '@/components/base/toast'
import { SingleColumnPage } from '@/components/page-layout'
import { Table } from '@/components/table/table'
import { useI18n } from '@/i18n/i18n-react'
import { PencilSquare } from '@/icons/pencil-square'
import { Trash } from '@/icons/trash'
import { ConditionalRouteComponent } from '@/utils/route-utilities'

type GiftCardRedemption = EndpointResponse<Endpoint.GetGiftCard>['redemptions'][number]

export const Route = createFileRoute('/_authenticated/_general/gift-cards/$giftCardUuid')({
	component: () => (
		<ConditionalRouteComponent routePath={Route.id} pathsToRenderOn={['/edit']}>
			<GiftCardDetail />
		</ConditionalRouteComponent>
	),
	loader: async ({ context, params: { giftCardUuid } }) => {
		await preloadGetGiftCard(context, { path: { giftCardUuid } })
	},
	staticData: {
		crumb: ({ context: { L } }) => L.GiftCards.Detail.Title()
	}
})

function GiftCardDetail() {
	const { L } = useI18n()
	const { giftCardUuid } = Route.useParams()

	const card = useGetGiftCard({ giftCardUuid })

	const navigate = useNavigate()
	const prompt = usePrompt()
	const deleteCard = useDeleteGiftCard(true)

	const handleDelete = async () => {
		const confirmed = await prompt({
			title: L.GiftCards.Delete.Title(),
			description: L.GiftCards.Delete.Description(),
			confirmText: L.General.Delete(),
			cancelText: L.General.Cancel()
		})
		if (!confirmed) return
		try {
			const result = await deleteCard.mutateAsync({ path: { giftCardUuid } })
			if (result.isSuccess) {
				toast.success(L.GiftCards.DeleteSuccess())
				navigate({ to: '/gift-cards' })
			} else {
				toast.error((result.response as { message?: string })?.message ?? L.Error.SomethingWentWrong())
			}
		} catch (e: any) {
			toast.error(e.message)
		}
	}

	const redemptionColumnDefs = useMemo<ColDef<GiftCardRedemption>[]>(
		() => [
			{
				headerName: L.GiftCards.Column.Amount(),
				field: 'amount',
				minWidth: 120,
				flex: 1,
				valueFormatter: ({ data }) =>
					data
						? L.Amount.Default({
								amount: data.amount,
								currencyCode: data.currencyCode
							})
						: ''
			},
			{
				headerName: L.GiftCards.Column.Status(),
				field: 'status',
				minWidth: 100,
				flex: 1,
				cellRenderer: ({ value }: { value: GiftCardRedemptionStatusType }) => (
					<StatusCell color={value === 'APPLIED' ? 'green' : 'orange'}>
						{L.Enum.RedemptionStatus(value)}
					</StatusCell>
				)
			},
			{
				headerName: L.GiftCards.Detail.Date(),
				field: 'createdAt',
				type: 'date',
				minWidth: 120,
				flex: 1
			}
		],
		[L]
	)

	return (
		<SingleColumnPage hasOutlet>
			{/* General */}
			<Container className='divide-y'>
				<ContainerHeader
					heading={card.data?.name || L.GiftCards.Detail.Title()}
					rightSide={
						<Link from={Route.fullPath} to='edit'>
							<IconButton tooltipText={L.General.Edit()}>
								<PencilSquare />
							</IconButton>
						</Link>
					}
					actions={[
						{
							actions: [
								{
									icon: <Trash />,
									label: L.General.Delete(),
									onClick: handleDelete
								}
							]
						}
					]}
				/>
				<SectionRow
					title={L.Fields.Status()}
					value={
						card.data?.status ? (
							<StatusCell color={getGiftCardStatusColor(card.data.status)}>
								{L.Enum.GiftCardStatus(card.data.status)}
							</StatusCell>
						) : (
							'-'
						)
					}
				/>
			</Container>

			{/* Card Details */}
			<Container className='divide-y'>
				<ContainerHeader heading={L.GiftCards.Detail.CardDetails()} />
				<SectionRow title={L.GiftCards.Column.Code()} value={card.data?.code ?? '-'} />
				<SectionRow
					title={L.GiftCards.Column.Amount()}
					value={
						card.data
							? L.Amount.Default({
									amount: card.data.amount,
									currencyCode: card.data.currencyCode
								})
							: '-'
					}
				/>
				<SectionRow
					title={L.Fields.CreatedAt()}
					value={card.data?.createdAt ? new Date(card.data.createdAt).toLocaleDateString() : '-'}
				/>
				<SectionRow
					title={L.Fields.UpdatedAt()}
					value={card.data?.updatedAt ? new Date(card.data.updatedAt).toLocaleDateString() : '-'}
				/>
			</Container>

			{/* Redemptions */}
			<Container className='flex flex-1 flex-col overflow-hidden'>
				<ContainerHeader heading={L.GiftCards.Detail.Redemptions()} />
				<Table<GiftCardRedemption>
					className='w-full min-w-[400px] flex-1'
					data={card.data?.redemptions}
					columnUtilities={{}}
					columnDefs={redemptionColumnDefs}
					animateRows
					suppressCellFocus
					rowToRouterConfig={redemption =>
						redemption.orderUuid
							? { to: '/orders/$orderUuid', params: { orderUuid: redemption.orderUuid } }
							: undefined
					}
				/>
			</Container>
		</SingleColumnPage>
	)
}

const getGiftCardStatusColor = (status: GiftCardStatusType): StatusColor => {
	switch (status) {
		case 'ACTIVE':
			return 'green'
		case 'RESERVED':
			return 'blue'
		case 'REDEEMED':
			return 'grey'
		default:
			return 'grey'
	}
}
